import { thousandsSeparators } from "../../../../config/utils";

export const STACK_LABEL_CLASS = "asset-chart-stack-label";

export const formatCostInMillions = value => `$ ${thousandsSeparators(((Number(value) || 0) / 1000000).toFixed(2))} M`;

export const formatValueAssetCount = point =>
    `${thousandsSeparators(point?.assetCount || 0)} / ${thousandsSeparators(point?.totalAssetCount ?? point?.assetCount ?? 0)}`;

export const buildTooltipLines = (color, lines = []) =>
    lines
        .filter(Boolean)
        .map(line => `<span style="color:${color || "#666666"}">●</span> ${line}`)
        .join("<br/>");

export const CLICK_TO_VIEW_DETAILS = "Click To View Details";

export const spreadStackedSegments = (chart, minLength) => {
    const yAxis = chart?.yAxis?.[0];
    if (!yAxis) return;
    const stackPoints = new Map();
    (chart.series || []).forEach(series => {
        if (!series.visible) return;
        (series.points || []).forEach(point => {
            if (!point?.graphic || !point.shapeArgs || !(point.y > 0)) return;
            if (!stackPoints.has(point.x)) stackPoints.set(point.x, []);
            stackPoints.get(point.x).push(point);
        });
    });
    const stackCollections = Object.values(yAxis.stacking?.stacks || {});
    stackPoints.forEach((points, x) => {
        points.sort((first, second) => second.shapeArgs.y + second.shapeArgs.height - (first.shapeArgs.y + first.shapeArgs.height));
        const originalTop = Math.min(...points.map(point => point.shapeArgs.y));
        let cursor = points[0].shapeArgs.y + points[0].shapeArgs.height;
        points.forEach(point => {
            const height = Math.max(point.shapeArgs.height, minLength);
            const y = cursor - height;
            point.shapeArgs = { ...point.shapeArgs, y, height };
            point.graphic.animate({ y, height }, chart.options?.chart?.animation);
            cursor = y;
        });
        const growth = originalTop - cursor;
        if (chart.inverted || !(growth > 0)) return;
        stackCollections.forEach(stackCollection => {
            const stackItem = stackCollection?.[x];
            const label = stackItem?.label;
            if (stackItem?.total > 0 && label?.alignAttr && label.alignAttr.y > -9999) label.attr({ y: label.alignAttr.y - growth });
        });
    });
};

export const buildStackAssetCounts = (series = []) =>
    (series || []).map(item =>
        (item?.data || []).map(point => ({
            assetCount: Number(point?.assetCount) || 0,
            totalAssetCount: Number(point?.totalAssetCount) || 0
        }))
    );

const getStackAssetCounts = (stackAssetCounts, chart, x) =>
    (stackAssetCounts || []).reduce(
        (totals, seriesCounts, seriesIndex) => {
            if (chart?.series?.[seriesIndex]?.visible === false) return totals;
            const counts = seriesCounts?.[x];
            if (!counts) return totals;
            return {
                assetCount: totals.assetCount + counts.assetCount,
                totalAssetCount: totals.totalAssetCount + counts.totalAssetCount
            };
        },
        { assetCount: 0, totalAssetCount: 0 }
    );

const STACK_LABEL_CHAR_WIDTH = 6.5;

const getStackLabelMaxChars = chart => {
    const axis = chart?.xAxis?.[0];
    const categoryCount = axis?.tickPositions?.length || axis?.categories?.length || 0;
    const plotWidth = chart?.plotWidth || 0;
    if (!plotWidth || !categoryCount) return Number.MAX_SAFE_INTEGER;
    return Math.floor(plotWidth / categoryCount / STACK_LABEL_CHAR_WIDTH);
};

const escapeAttribute = value => `${value ?? ""}`.replace(/"/g, "&quot;");

const STACK_LABEL_MIN_CHARS = 6;

export const buildStackTotalLabel = (total, stackAssetCounts, chart, x, { isValue = false } = {}) => {
    const costLabel = `cost: $${thousandsSeparators(((Number(total) || 0) / 1000000).toFixed(2))}M`;

    if (!isValue) return costLabel;

    const countLabel = `count: ${formatValueAssetCount(getStackAssetCounts(stackAssetCounts, chart, x))}`;

    const maxChars = getStackLabelMaxChars(chart);

    if (countLabel.length <= maxChars || maxChars < STACK_LABEL_MIN_CHARS) {
        return `${costLabel}<br/>${countLabel}`;
    }

    const clippedLabel = `${countLabel.slice(0, maxChars - 1)}…`;

    const anchorAttributes = `class="${STACK_LABEL_CLASS}" title="${escapeAttribute(countLabel)}"`;

    return `${costLabel}<br/><span ${anchorAttributes}>${clippedLabel}</span>`;
};

export const bindStackLabelTooltips = (chart, { onShow, onHide } = {}) =>
    setTimeout(() => {
        const root = chart?.renderTo || chart?.container;
        if (!root?.querySelectorAll || !onShow) return;
        root.querySelectorAll(`.${STACK_LABEL_CLASS}`).forEach(label => {
            if (label.dataset.stackLabelBound) return;
            label.dataset.stackLabelBound = "1";
            const fullText = label.getAttribute("title") || label.textContent.trim();
            label.removeAttribute("title");
            label.addEventListener("mouseenter", () => onShow(fullText, label));
            label.addEventListener("mouseleave", () => onHide?.());
        });
    }, 0);

export const TOP_N_OTHERS_NAME = "Others";
export const TOP_N_OTHERS_COLOR = "#9e9e9e";

export const DISABLED_SORT_BUTTONS = [
    { key: "name", label: "Name", active: true, desc: false, onClick: () => {} },
    { key: "value", label: "Value", active: false, desc: false, onClick: () => {} }
];

export const keepChartSettingsOpenOnChartClick = chartOrElement => {
    const container = chartOrElement?.container || chartOrElement;
    if (!container?.dataset || container.dataset.assetChartSettingsGuard) return;
    container.dataset.assetChartSettingsGuard = "1";
    container.addEventListener("click", event => {
        if (document.querySelector(".asset-chart-settings .chart-settings.show")) event.stopPropagation();
    });
};

export const sortChartPoints = (points = [], sortBy = "value", sortAsc = false) => {
    const direction = sortAsc ? 1 : -1;
    return [...(points || [])].sort((first, second) =>
        sortBy === "name"
            ? direction * String(first?.name ?? "").localeCompare(String(second?.name ?? ""), undefined, { numeric: true, sensitivity: "base" })
            : direction * ((Number(first?.y) || 0) - (Number(second?.y) || 0))
    );
};

export const parseTopN = value => {
    const topN = parseInt(value, 10);
    return Number.isFinite(topN) && topN > 0 ? topN : null;
};

export const isTopNApplied = (value, count) => {
    const topN = parseTopN(value);
    return !!topN && count > topN;
};

const toNumber = value => Number(value) || 0;

const sumBy = (rows, key) => rows.reduce((total, row) => total + toNumber(row?.[key]), 0);

const getTopIndexes = (values, topN) =>
    new Set(
        values
            .map((value, index) => ({ value, index }))
            .sort((a, b) => b.value - a.value)
            .slice(0, topN)
            .map(item => item.index)
    );

const getWeightedValue = (rows, weightKey) => {
    if (!rows.length) return 0;
    const weightTotal = weightKey ? sumBy(rows, weightKey) : 0;
    if (weightTotal > 0) {
        const weighted = rows.reduce((total, row) => total + toNumber(row?.y) * toNumber(row?.[weightKey]), 0);
        return Number((weighted / weightTotal).toFixed(2));
    }
    return Number((sumBy(rows, "y") / rows.length).toFixed(2));
};

export const applyTopNPoints = (points = [], value, { valueMode = "sum", weightKey = null, sumKeys = [] } = {}) => {
    const topN = parseTopN(value);
    if (!topN || points.length <= topN) return points;
    const topIndexes = getTopIndexes(
        points.map(point => toNumber(point?.y)),
        topN
    );
    const kept = points.filter((point, index) => topIndexes.has(index));
    const rest = points.filter((point, index) => !topIndexes.has(index));
    const othersValue = valueMode === "weighted" ? getWeightedValue(rest, weightKey) : sumBy(rest, "y");
    const others = {
        name: TOP_N_OTHERS_NAME,
        displayName: TOP_N_OTHERS_NAME,
        y: othersValue,
        valueSum: othersValue,
        color: TOP_N_OTHERS_COLOR,
        isOthers: true,
        othersMembers: rest.map(point => ({
            name: point?.displayName || point?.name,
            y: toNumber(point?.y),
            assetCount: toNumber(point?.assetCount),
            totalAssetCount: toNumber(point?.totalAssetCount),
            ...(point?.conditionName ? { conditionName: point.conditionName } : {}),
            ...(point?.conditionId ? { conditionId: point.conditionId } : {}),
            ...(point?.subSystemId ? { subSystemId: point.subSystemId } : {}),
            ...(point?.groupId ? { groupId: point.groupId } : {})
        }))
    };
    sumKeys.forEach(key => {
        others[key] = sumBy(rest, key);
    });
    return [...kept, others];
};

export const applyTopNSeries = (series = [], value) => {
    const topN = parseTopN(value);
    if (!topN || series.length <= topN) return series;
    const totals = series.map(item => sumBy(item?.data || [], "y"));
    const topIndexes = getTopIndexes(totals, topN);
    const kept = series.filter((item, index) => topIndexes.has(index));
    const rest = series.filter((item, index) => !topIndexes.has(index));
    const pointCount = Math.max(0, ...series.map(item => (item?.data || []).length));
    const othersData = Array.from({ length: pointCount }, (unused, pointIndex) => ({
        y: rest.reduce((total, item) => total + toNumber(item?.data?.[pointIndex]?.y), 0) || null,
        assetCount: rest.reduce((total, item) => total + toNumber(item?.data?.[pointIndex]?.assetCount), 0),
        totalAssetCount: rest.reduce((total, item) => total + toNumber(item?.data?.[pointIndex]?.totalAssetCount), 0),
        conditionName: TOP_N_OTHERS_NAME,
        conditionId: null,
        isOthers: true,
        othersMembers: rest.map(item => ({
            name: item?.name,
            y: toNumber(item?.data?.[pointIndex]?.y),
            assetCount: toNumber(item?.data?.[pointIndex]?.assetCount),
            totalAssetCount: toNumber(item?.data?.[pointIndex]?.totalAssetCount),
            conditionName: item?.data?.[pointIndex]?.conditionName ?? item?.name,
            conditionId: item?.data?.[pointIndex]?.conditionId ?? null
        }))
    }));
    return [
        ...kept,
        {
            name: TOP_N_OTHERS_NAME,
            data: othersData,
            color: TOP_N_OTHERS_COLOR,
            valueSum: sumBy(othersData, "y"),
            assetCount: sumBy(othersData, "assetCount"),
            isOthers: true,
            othersMembers: rest.map(item => ({
                name: item?.name,
                y: sumBy(item?.data || [], "y"),
                assetCount: sumBy(item?.data || [], "assetCount")
            }))
        }
    ];
};

export const getLegendValuesFromSeries = (series = []) =>
    series.map(item => ({
        name: item?.name,
        displayName: item?.displayName || item?.name,
        y: item?.data ? sumBy(item.data, "y") : toNumber(item?.y),
        assetCount: item?.data ? sumBy(item.data, "assetCount") : toNumber(item?.assetCount),
        totalAssetCount: item?.data ? sumBy(item.data, "totalAssetCount") : toNumber(item?.totalAssetCount)
    }));

export const isOthersPoint = point => !!point?.isOthers || point?.name === TOP_N_OTHERS_NAME;

export const getPieSeriesForPopup = point =>
    (point?.series?.data || [])
        .filter(slice => slice?.options)
        .map(slice => ({ name: slice.name, data: [{ ...slice.options, name: slice.name }] }));

export const getOthersPopupData = ({
    heading,
    type,
    chartView,
    category = "",
    point,
    groupLabel = null,
    groupFilterKey = null,
    groupIsCost = false
} = {}) => ({
    heading,
    type,
    chartView,
    ...(groupLabel ? { groupLabel } : {}),
    ...(groupFilterKey ? { groupFilterKey } : {}),
    ...(groupIsCost ? { groupIsCost } : {}),
    // On a bar/pie chart the clicked point's own category IS "Others" — only a real x category is a subtitle
    category: category === TOP_N_OTHERS_NAME ? "" : category,
    name: TOP_N_OTHERS_NAME,
    entityId: null,
    data: toNumber(point?.y),
    assetCount: toNumber(point?.assetCount),
    totalAssetCount: toNumber(point?.totalAssetCount),
    isOthers: true,
    othersRows: (point?.othersMembers || []).filter(row => row?.name)
});

/**
 * Places a chart popup next to whatever was clicked (bar/point) or outside the chart card
 * (axis/category clicks, `popupPosition.fromChartBox`). Shared by ChartDataPopUp and the
 * weighted-average gauge popup so both sit the same way.
 */
export const getChartPopupLayout = ({ popupPosition, isSingleView, popupHeight, popupWidth = 500, chartBoxGap = 20 } = {}) => {
    const baseStyle = { display: "block", cursor: "move" };
    // Maximized single chart keeps legacy fixed placement (chart-5, bottom-right)
    if (isSingleView || !popupPosition || typeof window === "undefined") {
        return { style: baseStyle, className: "" };
    }

    const gap = chartBoxGap;
    const gapRight = Number.isFinite(popupPosition.rightGap) ? popupPosition.rightGap : gap;
    const margin = 16;
    const popupHeightEstimate = Math.min(popupHeight || 320, window.innerHeight - margin * 2);
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

    let placement = "right";
    let preferredLeft;
    let preferredTop;
    let maxWidth;

    if (popupPosition.fromChartBox) {
        const rightEdge = popupPosition.right + gapRight + 1;
        const fitsRight = rightEdge + popupWidth <= viewportWidth - margin;
        const fitsLeft = popupPosition.left - gap - popupWidth >= margin;
        placement = fitsRight || !fitsLeft ? "right" : "left";
        preferredLeft = placement === "right" ? rightEdge : popupPosition.left - gap - popupWidth;
        maxWidth = placement === "right" ? Math.max(viewportWidth - preferredLeft - margin, 0) : undefined;
        preferredTop = popupPosition.top;
    } else {
        // Existing bar/point placement — next to the clicked graphic
        const barGap = 12;
        const hasSpaceRight = popupPosition.right + barGap + popupWidth <= viewportWidth - margin;
        const hasSpaceLeft = popupPosition.left - barGap - popupWidth >= margin;
        placement = hasSpaceRight || !hasSpaceLeft ? "right" : "left";
        preferredLeft = placement === "right" ? popupPosition.right + barGap : popupPosition.left - barGap - popupWidth;
        preferredTop = popupPosition.top + (popupPosition.height || 0) / 2 - popupHeightEstimate / 2;
    }

    const offsetLeft = popupPosition.fromChartBox ? 0 : 30;
    const offsetTop = popupPosition.fromChartBox ? 0 : 30;
    const left = popupPosition.fromChartBox
        ? Math.max(preferredLeft, margin)
        : Math.min(Math.max(preferredLeft, margin), Math.max(margin, viewportWidth - popupWidth - margin)) + offsetLeft;
    const top = popupPosition.fromChartBox
        ? Math.max(preferredTop, margin)
        : Math.min(Math.max(preferredTop, margin), Math.max(margin, viewportHeight - popupHeightEstimate - margin)) + offsetTop;

    return {
        className: `positioned-chart-popup positioned-chart-popup-${placement}`,
        style: {
            ...baseStyle,
            left: `${left}px`,
            top: `${top}px`,
            right: "auto",
            bottom: "auto",
            ...(maxWidth ? { maxWidth: `${maxWidth}px` } : {}),
            "--asset-chart-popup-left": `${left}px`,
            "--asset-chart-popup-top": `${top}px`
        }
    };
};

export const applyChartPopupPosition = (node, options = {}) => {
    if (!node) return;
    const scroller = node.querySelector?.(".table-section");
    const scrollTop = scroller?.scrollTop || 0;
    applyChartPopupPositionUnscrolled(node, options);
    if (scroller && scrollTop) scroller.scrollTop = scrollTop;
};

const applyChartPopupPositionUnscrolled = (node, { getAnchorPosition, popupPosition, isSingleView, popupWidth, chartBoxGap } = {}) => {
    if (isSingleView) {
        if (node.classList.contains("view_chart_asset")) placeChartPopupInCardCorner(node);
        return;
    }
    const livePosition = getAnchorPosition?.() || popupPosition;
    if (!livePosition) return;
    node.style.maxHeight = "";
    node.classList.remove("asset-chart-popup-fit");
    const scale = getChartPopupScale(node);
    const { style, className } = getChartPopupLayout({
        popupPosition: livePosition,
        isSingleView,
        popupHeight: node.offsetHeight * scale,
        popupWidth: (popupWidth || 500) * scale,
        chartBoxGap
    });
    if (!style.left || !style.top) return;
    const toStylePx = value => `${Math.round(parseFloat(value) / scale)}px`;
    node.classList.remove("positioned-chart-popup-left", "positioned-chart-popup-right");
    className.split(" ").forEach(name => name && node.classList.add(name));
    node.style.left = toStylePx(style.left);
    node.style.top = toStylePx(style.top);
    node.style.maxWidth = style.maxWidth ? toStylePx(style.maxWidth) : "";
    node.style.setProperty("--asset-chart-popup-left", toStylePx(style["--asset-chart-popup-left"]));
    node.style.setProperty("--asset-chart-popup-top", toStylePx(style["--asset-chart-popup-top"]));
    const maxHeight = node.classList.contains("view_chart_asset") ? getChartPopupMaxHeight(node) : "";
    node.style.maxHeight = maxHeight;
    node.classList.toggle("asset-chart-popup-fit", !!maxHeight);
};


const getChartPopupMaxHeight = node => {
    const body = node.closest?.(".asset-charts-body");
    if (!body) return "";
    const edges = [
        body,
        body.querySelector(".asset-charts-scroll-section"),
        body.querySelector(".chart-ground.align-base"),
        body.querySelector(".asset-dashboard-map-panel > .sld-ara")
    ]
        .filter(Boolean)
        .map(el => el.getBoundingClientRect().bottom)
        .filter(Number.isFinite);
    const limitBottom = Math.min(window.innerHeight || document.documentElement.clientHeight, ...edges);
    const popupRect = node.getBoundingClientRect();
    const room = Math.floor((limitBottom - popupRect.top) / getChartPopupScale(node));
    return room > 0 ? `${room}px` : "";
};

const placeChartPopupInCardCorner = (node, inset = 8) => {
    const body = node.closest?.(".asset-charts-body");
    const card = body?.querySelector("#chartItem") || body?.querySelector(".chart-area");
    const cardRect = card?.getBoundingClientRect();
    if (!cardRect || !cardRect.width || !cardRect.height) return;

    node.style.maxHeight = window.innerWidth <= 1679 ? "500px" : "650px";
    node.classList.add("positioned-chart-popup", "asset-chart-popup-fit");
    const scale = getChartPopupScale(node);
    const height = node.offsetHeight * scale;
    const width = node.offsetWidth * scale;

    const top = cardRect.bottom - inset - height;
    const left = cardRect.right - inset - width;
    const toStylePx = value => `${Math.round(value / scale)}px`;
    node.style.left = toStylePx(left);
    node.style.top = toStylePx(top);
    node.style.maxWidth = "";
    node.style.setProperty("--asset-chart-popup-left", toStylePx(left));
    node.style.setProperty("--asset-chart-popup-top", toStylePx(top));
};

const getChartPopupScale = node => {
    const rectWidth = node.getBoundingClientRect().width;
    const scale = node.offsetWidth && rectWidth ? rectWidth / node.offsetWidth : 1;
    return Number.isFinite(scale) && scale > 0 ? scale : 1;
};

export const watchChartPopupPosition = (reposition, getPopupNode) => {
    let frame = null;
    const onLayoutChange = event => {
        if (event?.type === "scroll" && getPopupNode?.()?.contains?.(event.target)) return;
        if (frame) return;
        frame = requestAnimationFrame(() => {
            frame = null;
            reposition();
        });
    };
    window.addEventListener("scroll", onLayoutChange, true);
    window.addEventListener("resize", onLayoutChange);
    return () => {
        if (frame) cancelAnimationFrame(frame);
        window.removeEventListener("scroll", onLayoutChange, true);
        window.removeEventListener("resize", onLayoutChange);
    };
};

export const getColumnAxisScroll = (categoryCount = 0, maxVisible = 20) => {
    const isScroll = categoryCount > maxVisible;
    return {
        min: 0,
        max: Math.max((isScroll ? maxVisible : categoryCount) - 1, 0),
        scrollbar: { enabled: isScroll }
    };
};
